import { Controller } from '@hotwired/stimulus';
import { AutomationRules } from '../lib/automation_rules.js';

// The page owns draft state. Drawers edit copies and apply changes through events;
// only the parent form submits conditions and the selected effect to Rails.
export default class extends Controller {
  static targets = ['mode', 'conditions', 'effect', 'conditionTemplate', 'effectTemplate', 'effectId', 'effectAction',
    'rule', 'error', 'debugRules', 'draft', 'reset', 'addCondition', 'selectEffect'];
  static values = { initial: String, schema: Object, effect: Object, avatars: Array, sounds: Array, actions: Array };

  connect() {
    this.schema = this.schemaValue;
    this.statements = [];
    this.effectDraft = this.effectValue;
    this.invalidInitial = false;
    this.conditionIndex = null;
    try {
      if (this.draftTarget.value) {
        const draft = JSON.parse(this.draftTarget.value);
        this.statements = draft.statements;
        this.effectDraft = draft.effect;
        this.schema = draft.schema;
        this.modeTarget.value = draft.mode;
        this.invalidInitial = draft.invalidInitial;
      } else {
        const rule = JSON.parse(this.initialValue || 'null');
        const initial = rule ? AutomationRules.decode(rule, this.schema) : { mode: 'and', statements: [] };
        this.statements = initial.statements;
        this.modeTarget.value = initial.mode;
      }
    } catch (error) {
      this.invalidInitial = true;
      this.errorTarget.textContent = error.message;
    }
    this.renderConditions();
    this.renderEffect();
    this.refreshRule();
  }

  openCondition(event) {
    this.conditionIndex = this.index(event);
    if (this.invalidInitial || (this.conditionIndex === null && this.statements.length >= 20)) {
      event.preventDefault();
      this.errorTarget.textContent = this.invalidInitial ? 'Reset the unsupported conditions before replacing them.' : 'Use at most 20 conditions.';
    }
  }

  populateCondition(event) {
    this.populate(event.target, { statement: this.statements[this.conditionIndex] });
  }

  saveCondition(event) {
    const { statement, options } = event.detail;
    const variables = AutomationRules.targetsFor(statement);
    // Retain choices discovered by the async selector for validation and summaries.
    for (const target of this.schema.targets.filter(target => variables.includes(target.variable) ||
      (target.field === 'conditions' && variables.some(variable => variable.endsWith('.status_condition_ids'))))) {
      target.options = options;
      target.values = options.map(option => option.value);
    }
    if (this.conditionIndex === null) this.statements.push(statement);
    else this.statements[this.conditionIndex] = statement;
    this.renderConditions();
    this.refreshRule();
  }

  removeCondition(event) {
    this.statements.splice(this.index(event), 1);
    this.renderConditions();
    this.refreshRule();
  }

  populateEffect(event) { this.populate(event.target, { effect: this.effectDraft }); }

  saveEffect(event) {
    this.effectDraft = { ...event.detail.effect };
    this.renderEffect();
    this.refreshRule();
  }

  removeEffect() {
    this.effectDraft = { automatable_effect: this.effectValue.automatable_effect };
    this.renderEffect();
    this.refreshRule();
  }

  index(event) {
    const tile = event.currentTarget.closest('[data-step-tile]');
    return tile ? Number(tile.dataset.index) : null;
  }

  populate(element, detail) {
    element.dispatchEvent(new CustomEvent('automation-editor:populate', { detail }));
  }

  renderConditions() {
    this.conditionsTarget.querySelectorAll('[data-step-tile]').forEach(tile => tile.remove());
    this.statements.forEach((statement, index) => {
      const targets = AutomationRules.targetsFor(statement).map(variable => this.schema.targets.find(target => target.variable === variable));
      const target = targets[0];
      const labels = statement.values.map(value => target?.options.find(option => JSON.stringify(option.value) === JSON.stringify(value))?.label || `Missing value: ${value?.var || JSON.stringify(value)}`);
      const characterMatch = statement.groupMatch ? `${{ any: 'Any character', all: 'All characters', none: 'No characters' }[statement.groupMatch]} · ` : '';
      const description = `${characterMatch}${this.schema.comparisonLabels[statement.comparison]} ${labels.join(', ')}`;
      const title = targets.length === 1 ? target?.label || 'Missing character' :
        `Any of ${targets.map(target => target?.characterLabel || 'Missing character').join(', ')} · ${{ turn: 'Active turn', focused: 'Focused', conditions: 'Status conditions' }[target?.field]}`;
      this.addConditionTarget.before(this.tile(this.conditionTemplateTarget, index, title, description));
    });
  }

  renderEffect() {
    this.effectTarget.querySelectorAll('[data-step-tile]').forEach(tile => tile.remove());
    const effect = this.effectDraft;
    this.effectIdTarget.value = effect.automatable_id ?? '';
    this.effectActionTarget.value = effect.automatable_effect ?? '';
    this.selectEffectTarget.hidden = Boolean(effect.automatable_id);
    if (!effect.automatable_id) return;

    const avatar = this.avatarsValue.find(avatar => avatar.id === Number(effect.automatable_id));
    const sound = this.soundsValue.find(sound => sound.id === Number(effect.automatable_id));
    const description = effect.automatable_effect === 'sound_effect.play_once' ? sound?.name : avatar && `${avatar.character} · ${avatar.name}`;
    const action = this.actionsValue.find(action => action.automatable_effect === effect.automatable_effect);
    this.selectEffectTarget.before(this.tile(this.effectTemplateTarget, 0,
      action?.label || 'Choose an effect',
      description || 'Missing selection — edit this effect to choose another.'));
  }

  tile(template, index, title, description) {
    const tile = template.content.firstElementChild.cloneNode(true);
    tile.dataset.index = index;
    tile.querySelector('[data-step-title]').textContent = title;
    tile.querySelector('[data-step-description]').textContent = description;
    return tile;
  }

  generatedRule() {
    if (this.invalidInitial) throw new Error('Reset the unsupported conditions before replacing them.');
    return AutomationRules.encode(this.modeTarget.value, this.statements, this.schema);
  }

  showDebug(rule) {
    if (!this.hasDebugRulesTarget) return;

    this.debugRulesTarget.value = JSON.stringify(rule, null, 2);
    this.debugRulesTarget.dispatchEvent(new Event('input', { bubbles: true }));
  }

  debugRulesTargetConnected() {
    // The lazy frame may arrive after several unsaved condition changes.
    if (this.statements) this.refreshRule();
  }

  refreshRule() {
    this.resetTarget.hidden = !this.invalidInitial;
    this.draftTarget.value = JSON.stringify({ mode: this.modeTarget.value, statements: this.statements,
      effect: this.effectDraft, schema: this.schema, invalidInitial: this.invalidInitial });
    try {
      const rule = this.statements.length || this.invalidInitial ? this.generatedRule() : null;
      this.showDebug(rule);
      this.errorTarget.textContent = '';
    } catch (error) {
      this.showDebug(null);
      this.errorTarget.textContent = error.message;
    }
  }

  reset() {
    this.invalidInitial = false;
    this.statements = [];
    this.modeTarget.value = 'and';
    this.renderConditions();
    this.refreshRule();
  }

  serialize(event) {
    try {
      // Debugger edits are temporary; saved rules always come from the tiles.
      this.ruleTarget.value = JSON.stringify(this.generatedRule());
      if (!this.effectDraft.automatable_id || !this.actionsValue.some(action => action.automatable_effect === this.effectDraft.automatable_effect)) {
        throw new Error('Select an effect and its avatar or sound.');
      }
      this.errorTarget.textContent = '';
    } catch (error) {
      event.preventDefault();
      this.errorTarget.textContent = error.message;
    }
  }
}
